import { useCallback, useRef } from "react"
import { useTranslation } from "react-i18next"
import {
  BadgeWithInput,
  createEditableBadgeStore,
  EditableBadge,
  EditableBadgeContext,
} from "@/components/shared/EditableBadge"
import {
  useCreateOpexCategory,
  useUpdateOpexSelectedCategory,
  useGetOpexCategories,
  useDeleteOpexCategory,
  useGetOpexSelectedCategories,
} from "@/feature/financial-model/ecommerce/opex/api"
import {
  OPEX_GRANULARITY_LEVELS,
  OPEX_MODELLING_APPROACH,
} from "@/feature/financial-model/ecommerce/opex/constants.ts"
import { CreateCustomOpexCategoryRequest } from "@/feature/financial-model/ecommerce/opex/api/types.ts"
import { Show } from "@/components/shared/show.tsx"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

export const CategoryBadgesContent = ({
  financialModelId,
  onNewCategoryAdded,
}: {
  financialModelId: string
  onNewCategoryAdded: (name: string) => void
}) => {
  const { t } = useTranslation(`opex`)
  const { data: opexCategories } = useGetOpexCategories({ financialModelId })
  const { data: opexSelectedCategories } = useGetOpexSelectedCategories({
    financialModelId,
  })

  const { mutate: updateOpexCategory } = useUpdateOpexSelectedCategory()

  const { mutateAsync: createOpexCategory } = useCreateOpexCategory()

  const { mutate: deleteOpexCategory } = useDeleteOpexCategory()

  const store = useRef(
    createEditableBadgeStore({ items: opexCategories })
  ).current

  const toggleOpexCategory = useCallback(
    (id: string) => {
      const selectedCategory = opexSelectedCategories.find(
        (cat) => cat.opex_category.id === id
      )
      if (selectedCategory) {
        if (selectedCategory.opex_category.category_type === `custom`) {
          deleteOpexCategory({
            financialModelId,
            categoryId: id,
          })
        } else {
          updateOpexCategory({
            financialModelId,
            opex_category_id: id,
            is_selected: !selectedCategory.is_selected,
            name: selectedCategory.opex_category.name,
            modeling_approach: selectedCategory.modeling_approach,
          })
        }
      } else {
        const category = opexCategories?.find((cat) => cat.id === id)
        updateOpexCategory({
          financialModelId,
          opex_category_id: id,
          is_selected: true,
          name: category?.name ?? ``,
          modeling_approach: OPEX_MODELLING_APPROACH.ABSOLUTE_VALUE,
        })
      }
    },
    [
      deleteOpexCategory,
      financialModelId,
      opexCategories,
      opexSelectedCategories,
      updateOpexCategory,
    ]
  )

  const updateOpexCategoryBadge = useCallback(
    (items: { id: string; name: string; is_selected: boolean }[]) => {
      const request = items.map((item) => ({
        financialModelId,
        opex_category_id: item.id,
        is_selected: item.is_selected,
        name: item.name,
        modeling_approach: OPEX_MODELLING_APPROACH.ABSOLUTE_VALUE,
      }))
      updateOpexCategory(request[0])
    },
    [financialModelId, updateOpexCategory]
  )

  const handleCreateCategory = useCallback(
    async (data: CreateCustomOpexCategoryRequest) => {
      const newCategory = await createOpexCategory(data)
      onNewCategoryAdded(newCategory.opex_category.name)
      return {
        name: newCategory.opex_category.name,
        is_selected: true,
        id: newCategory.opex_category.id,
        modeling_approach: newCategory.modeling_approach,
        granularity_level: newCategory.granularity_level,
        financial_model_id: financialModelId,
        type: `custom` as const,
      }
    },
    [createOpexCategory, financialModelId, onNewCategoryAdded]
  )

  const createOpexPayload = useCallback(
    (name: string) => ({
      name,
      modeling_approach: OPEX_MODELLING_APPROACH.ABSOLUTE_VALUE,
      granularity_level: OPEX_GRANULARITY_LEVELS.AGGREGATE_EXPENSES,
      financialModelId,
    }),
    [financialModelId]
  )

  return (
    <EditableBadgeContext.Provider value={store}>
      <TooltipProvider>
        <div className="flex flex-wrap gap-2 mt-4">
          {opexCategories?.map((category) => (
            <Show
              when={category.type !== `required`}
              key={category.id}
              fallback={
                <Tooltip>
                  <TooltipTrigger>
                    <EditableBadge
                      key={category.id}
                      item={category}
                      isSelected={opexSelectedCategories.some(
                        (cat) => cat.opex_category.id === category.id
                      )}
                      toggle={toggleOpexCategory}
                      update={updateOpexCategoryBadge}
                    />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    {t(`opex.setup.tooltip.locked_categories`)}
                  </TooltipContent>
                </Tooltip>
              }
            >
              <EditableBadge
                key={category.id}
                item={category}
                isSelected={opexSelectedCategories?.some(
                  (cat) => cat.opex_category.id === category.id
                )}
                toggle={toggleOpexCategory}
                update={updateOpexCategoryBadge}
              />
            </Show>
          ))}
          <BadgeWithInput
            onCreateCategory={handleCreateCategory}
            createPayload={createOpexPayload}
          />
        </div>
      </TooltipProvider>
    </EditableBadgeContext.Provider>
  )
}
